import { useState, useContext, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useLang } from '_metronic/i18n/Metronici18n'
import { useQuotationStore } from 'Store/Quotation'
import { useNavigate } from 'react-router-dom'
import { useKumopackChat } from 'Hooks/useKumopackChat'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { QuotationFormContext } from '../Context'
import { shallow } from 'zustand/shallow'

import TitleCell from '../Components/TitleCell'
import QuantityInputCell from '../Components/QuantityInputCell'
import QuotePriceCell from '../Components/QuotePriceCell'
import TotalPriceCell from 'Presentation/Components/RequestCard/Components/TotalPriceCell'
import MultiplePriceCell from 'Presentation/Components/RequestCard/Components/MultiplePrice'

import { ProductionUnit, ThProductionUnit } from 'Configuration/ProductionUnit'
import { ProductionMeasurement, ThProductionMeasurement } from 'Configuration/ProductionMeasurement'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { generateImageUrl } from 'Utils/generateImageUrl'

const ViewModel = () => {
	const currentLanguage = useLang()
	const location = useLocation()
	const navigate = useNavigate()
	const { id } = useParams()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { updateLoading, updateError, updateSorting } = useContext(TableContext)
	const {
		setShowItemTypeSelection,
		setShowQuotationUrlModal,
		setSelectedProductLine,
		setSelectedProductCategory,
	} = useContext(QuotationFormContext)

	const { selected, getOne, create, update, publish, clearState } = useQuotationStore(
		state => ({
			selected: state.selected,
			getOne: state.getOne,
			create: state.create,
			update: state.updateSupplierCreated,
			publish: state.publish,
			clearState: state.clearState,
		}),
		shallow
	)
	const { chatSupplierToBuyer } = useKumopackChat()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [items, setItems] = useState<any[]>([])
	const [financial, setFinancial] = useState<{
		discount: string
		isVatCalculated: boolean
	}>({
		discount: '',
		isVatCalculated: true,
	})

	const [hasChanged, setHasChanged] = useState(false)

	const [selectingProductFormType, setSelectingProductFormType] = useState<'create' | 'edit'>(
		'create'
	)
	const [isSelectingProduct, setIsSelectingProduct] = useState(false)
	const [selectedProductIdx, setSelectedProductIdx] = useState<number | null>(null)
	const [selectedProduct, setSelectedProduct] = useState<any | null>(null)

	const [generatedUrl, setGeneratedUrl] = useState<string>('')

	const isCreate = location.pathname.includes('create')

	const orderedItems = useMemo(() => {
		const fixedItems = items.filter(item => item.isFixed)
		const nonFixedItems = items.filter(item => !item.isFixed)

		return [...fixedItems, ...nonFixedItems]
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items])
	const canSelectProductLine = useMemo(() => {
		return (
			orderedItems.filter(item => {
				return item.isFixed && item.product !== null
			}).length === 0
		)
	}, [orderedItems])

	const documentStatusStyles = useMemo(() => {
		if (isCreate) {
			return {
				title: 'Draft',
				className: 'btn btn-sm white-button text-kumpack-grey-900 fw-bold',
			}
		} else {
			if (Object.keys(selected).length === 0) {
				return {
					title: 'กำลังโหลดข้อมูล',
					className: 'btn btn-sm btn-kumopack-secondary-50 text-kumopack-secondary-700 fw-bold',
				}
			}
			if (selected?.documentStatus === '10') {
				return {
					title: 'รอดำเนินการ',
					className: 'btn btn-sm btn-kumopack-warning-50 text-kumopack-warning-600 fw-bold',
				}
			} else if (selected?.isPublic) {
				return {
					title: 'ส่งใบเสนอราคาแล้ว',
					className: 'btn btn-sm btn-kumopack-success-50 text-kumopack-success-600 fw-bold',
				}
			} else {
				return {
					title: currentLanguage === 'en' ? selected?.statusTitleEn : selected?.statusTitleTh,
					className: 'btn btn-sm btn-kumopack-secondary-700 text-kumopack-base-white fw-bold',
				}
			}
		}
	}, [isCreate, selected, currentLanguage])

	// Statuses for created quotation
	const canGenerateQuotation = useMemo(() => {
		return isCreate === false && selected?.buyerId === null
	}, [isCreate, selected])
	const canEditQuotation = useMemo(() => {
		return (
			isCreate ||
			(selected?.documentStatus === '10' &&
				selected?.buyerId == null &&
				selected?.isPublic === false)
		)
	}, [isCreate, selected])
	const isAccepted = useMemo(() => {
		return selected?.buyerId !== null && selected?.documentStatus === '40'
	}, [selected])

	const pageTitle = useMemo(() => {
		if (isCreate) {
			if (currentLanguage === 'en') {
				return 'Create Quotation'
			} else {
				return 'สร้างใบเสนอราคา'
			}
		} else {
			if (currentLanguage === 'en') {
				return 'Quotation Detail'
			} else {
				return 'รายละเอียดใบเสนอราคา'
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isCreate, currentLanguage])
	const pageBreachCrumbs = useMemo(() => {
		const breadCrumbs = [
			{
				title: 'Documents',
				path: '/documents',
				isActive: false,
				isSeparator: false,
			},
			{
				title: 'Quotation',
				path: '/documents/quotation',
				isActive: false,
				isSeparator: false,
			},
		]

		if (isCreate) {
			breadCrumbs.push({
				title: 'Create',
				path: '#',
				isActive: true,
				isSeparator: false,
			})
		} else {
			breadCrumbs.push({
				title: 'Detail',
				path: '#',
				isActive: true,
				isSeparator: false,
			})
		}

		return breadCrumbs
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isCreate])

	const netBeforeVat = useMemo(() => {
		const totalItemsPrice = items.reduce((acc, item) => {
			if (item.isDelete) return acc

			if (item.quotePrice === null || item.quotePrice === '') {
				return acc
			} else {
				return acc + parseFloat(item.quotePrice) * parseFloat(item.quantity)
			}
		}, 0)

		let sumPrice = totalItemsPrice

		if (financial.discount && financial.discount !== '' && !isNaN(parseFloat(financial.discount))) {
			sumPrice = sumPrice - parseFloat(financial.discount)
		}

		return sumPrice.toFixed(2)
	}, [items, financial])

	const net = useMemo(() => {
		const totalItemsPrice = items.reduce((acc, item) => {
			if (item.isDelete) return acc

			if (item.quotePrice === null || item.quotePrice === '') {
				return acc
			} else {
				return acc + parseFloat(item.quotePrice) * parseFloat(item.quantity)
			}
		}, 0)

		let sumPrice = totalItemsPrice

		if (financial.discount && financial.discount !== '' && !isNaN(parseFloat(financial.discount))) {
			sumPrice = sumPrice - parseFloat(financial.discount)
		}

		if (financial.isVatCalculated) {
			return (sumPrice * 1.07).toFixed(2)
		} else {
			return sumPrice.toFixed(2)
		}
	}, [items, financial])

	const vatAmount = useMemo(() => {
		const totalItemsPrice = items.reduce((acc, item) => {
			if (item.isDelete) return acc
			if (item.isFixed) {
				if (item.quotePrice === null || item.quotePrice === '') {
					return acc
				} else {
					return acc + parseFloat(item.quotePrice) * parseFloat(item.quantity)
				}
			} else {
				if (item.quotePrice === null || item.quotePrice === '') {
					return acc
				} else {
					return acc + parseFloat(item.quotePrice) * parseFloat(item.quantity)
				}
			}
		}, 0)

		let sumPrice = totalItemsPrice

		if (financial.discount && financial.discount !== '' && !isNaN(parseFloat(financial.discount))) {
			sumPrice = sumPrice - parseFloat(financial.discount)
		}

		if (financial.isVatCalculated) {
			return (sumPrice * 0.07).toFixed(2)
		} else {
			return '0.00'
		}
	}, [items, financial])

	const acceptedBuyer = useMemo(() => {
		if (selected?.buyer) {
			return {
				...selected?.buyer,
				pictureProfilePath: selected?.buyer?.pictureProfilePath
					? generateImageUrl(selected?.buyer?.pictureProfilePath)
					: null,
			}
		} else {
			return {}
		}
	}, [selected])

	const onChatBuyer = () => {
		chatSupplierToBuyer(selected?.buyer?.uuId)
	}

	const fetchData = () => {
		if (!isCreate && id) {
			updateLoading(true)
			setIsLoading(true)

			getOne(id).then(({ success, data }) => {
				if (!success) {
					toast.error(`ไม่สามารถดึงข้อมูลใบเสนอราคา ${data}`)
					updateError(true)
				} else {
					setIsLoading(false)
					updateLoading(false)

					const { discount, isVatCalculated, items } = data

					// Set financial
					setFinancial({
						discount: discount ? parseFloat(discount).toFixed(2) : '',
						isVatCalculated,
					})

					// Set items
					let newItems: any[] = []
					// Editable
					if (data?.isPublic === false && data?.documentStatus === '10') {
						newItems = items.reduce((acc: any, item: any) => {
							const findedOemRequestItem = (data?.inquiry?.oemRequest?.items).find(
								(oItem: any) => parseInt(oItem.id) === item.oemRequestItemId
							)

							let disableRemark = false
							if (item?.isFixed) {
								const finded = acc.find(
									(i: any) =>
										i.product?.id === item.productId && i.oemRequestItemId === item.oemRequestItemId
								)
								if (finded) {
									disableRemark = true
								}
							}

							acc.push({
								...item,
								completed: true,
								disableRemark,
								isPublic: data?.isPublic,
								documentStatus: data.documentStatus,
								dimension: findedOemRequestItem?.dimension ?? undefined,
								product: findedOemRequestItem?.product ?? undefined,
								options: findedOemRequestItem?.options ?? [],
								accessories: findedOemRequestItem?.accessories ?? [],
								thickness: findedOemRequestItem?.thickness ?? undefined,
								outerMaterial: findedOemRequestItem?.outerMaterial ?? undefined,
								innerMaterial: findedOemRequestItem?.innerMaterial ?? undefined,
								printingOption: findedOemRequestItem?.printingOption ?? undefined,
								forming: findedOemRequestItem?.forming ?? undefined,
							})

							return acc
						}, [])
					}
					// Non editable
					else {
						newItems = items.reduce((acc: any[], item: any) => {
							const findedOemRequestItem = (data?.inquiry?.oemRequest?.items).find(
								(oItem: any) => parseInt(oItem.id) === item.oemRequestItemId
							)

							if (item.isFixed) {
								const added = acc.find(
									(i: any) =>
										i.product?.id === item.productId && i.oemRequestItemId === item.oemRequestItemId
								)

								if (added) {
									const index = acc.findIndex(
										(i: any) =>
											i.product?.id === item.productId &&
											i.oemRequestItemId === item.oemRequestItemId
									)

									acc[index].quantities.push({
										quantity: item.quantity,
										quotePrice: item.quotePrice,
									})
								} else {
									acc.push({
										...item,
										completed: true,
										isPublic: data?.isPublic,
										quantities: [{ quantity: item.quantity, quotePrice: item.quotePrice }],
										documentStatus: data.documentStatus,
										dimension: findedOemRequestItem?.dimension ?? undefined,
										product: findedOemRequestItem?.product ?? undefined,
										options: findedOemRequestItem?.options ?? [],
										accessories: findedOemRequestItem?.accessories ?? [],
										thickness: findedOemRequestItem?.thickness ?? undefined,
										outerMaterial: findedOemRequestItem?.outerMaterial ?? undefined,
										innerMaterial: findedOemRequestItem?.innerMaterial ?? undefined,
										printingOption: findedOemRequestItem?.printingOption ?? undefined,
										forming: findedOemRequestItem?.forming ?? undefined,
									})
								}
							} else {
								acc.push({
									...item,
									completed: true,
									isPublic: data?.isPublic,
									quantities: [{ quantity: item.quantity, quotePrice: item.quotePrice }],
									documentStatus: data.documentStatus,
									dimension: findedOemRequestItem?.dimension ?? undefined,
									product: findedOemRequestItem?.product ?? undefined,
									options: findedOemRequestItem?.options ?? [],
									accessories: findedOemRequestItem?.accessories ?? [],
									thickness: findedOemRequestItem?.thickness ?? undefined,
									outerMaterial: findedOemRequestItem?.outerMaterial ?? undefined,
									innerMaterial: findedOemRequestItem?.innerMaterial ?? undefined,
									printingOption: findedOemRequestItem?.printingOption ?? undefined,
									forming: findedOemRequestItem?.forming ?? undefined,
								})
							}

							return acc
						}, [])
					}
					setItems(newItems)

					// Set Selected Product Line and Product Category
					const findedProduct = newItems.find((item: any) => item.product !== null)
					const productLine =
						findedProduct?.product?.productLine?.parent?.id ??
						findedProduct?.product?.productLine?.id
					const productCategory = findedProduct?.product?.productLine?.parent?.id
						? findedProduct?.product?.productLine?.id
						: 0
					setSelectedProductLine(productLine)
					setSelectedProductCategory(productCategory)
				}
			})
		}
	}

	const setupTable = () => {
		updateLoading(false)
		updateError(false)
	}

	const onClickAddNewItem = () => {
		setShowItemTypeSelection(true)
	}

	const onConfirmAddProduct = (input: {
		product: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
			code: string
			quantity: number
			shortDescription: string

			productLine?: {
				id: number
				nameEn: string
				nameTh: string
				featurePicturePath: string | null
				parent?: {
					id: number
					nameEn: string
					nameTh: string
					featurePicturePath: string | null
				}
			}
		}

		dimension?: {
			width: number
			length: number
			height: number
			weightGram: number
			verticalStack: number
			unit: ProductionUnit | ThProductionUnit
			measurementApproach: ProductionMeasurement | ThProductionMeasurement
		}
		options?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}[]
		accessories?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}[]
		thickness?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		outerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		innerMaterial?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		printingOption?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
		forming?: {
			id: number
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}) => {
		const itemsCopy = [...items]
		const selectedIdx = selectedProductIdx as number

		itemsCopy[selectedIdx] = {
			completed: true,
			product: input.product,
			dimension: input.dimension,
			options: (input.options ?? []).map(o => ({
				taxonomyProductLineOption: {
					...o,
				},
			})),
			accessories: (input.accessories ?? []).map(a => ({
				taxonomyProductLineAccessory: {
					...a,
				},
			})),
			thickness: input.thickness,
			outerMaterial: input.outerMaterial,
			innerMaterial: input.innerMaterial,
			printingOption: input.printingOption,
			forming: input.forming,
			quantity: itemsCopy[selectedIdx]?.quantity ?? 0,
			requestPrice: itemsCopy[selectedIdx]?.requestPrice ?? 0,
			title: '',
			quotePrice: itemsCopy[selectedIdx]?.quotePrice ?? '',
			remark: '',
			disableRemark: false,
			isFixed: true,
			isDelete: false,
			isCreate: false,
		}

		setItems(itemsCopy)
	}

	const onAddNewItem = (type: 'product' | 'additional') => {
		const newItems = [
			...items,
			{
				completed: type === 'additional',
				product: null,
				quantity: 0,
				requestPrice: 0,
				title: '',
				quotePrice: '',
				remark: '',
				isFixed: type === 'product',
				isDelete: false,
				isCreate: true,
			},
		]

		setItems(newItems)
		setShowItemTypeSelection(false)

		if (type === 'product') {
			setSelectedProductIdx(newItems.length - 1)
			setIsSelectingProduct(true)
			if (canSelectProductLine) {
				setSelectedProductLine(0)
			}
			setSelectingProductFormType('create')
			setSelectedProduct(null)
		}

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onBackFromSelectNewItem = () => {
		if (items.length >= 1) {
			setItems(prevState => prevState.filter(item => item.completed === true))
		}
	}

	const onChangeDiscount = (value: string) => {
		const isNumber = /^[0-9]*\.?[0-9]*$/.test(value)
		if (!isNumber) {
			return
		}

		setFinancial({
			...financial,
			discount: value,
		})

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onChangeVatCalculated = (value: boolean) => {
		if (isCreate || (selected?.documentStatus === '10' && selected?.isPublic === false)) {
			setFinancial({
				...financial,
				isVatCalculated: value,
			})

			if (!hasChanged) {
				setHasChanged(true)
			}
		}
	}

	const onChangeItemRemark = (value: string, index: number) => {
		const newItems = [...orderedItems]
		newItems[index].remark = value
		setItems(newItems)

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onChangeItemTitle = (value: string, index: number) => {
		const newItems = [...orderedItems]
		newItems[index].title = value
		setItems(newItems)

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onChangeQuantity = (value: string, index: number) => {
		const isNumber = /^[0-9]*$/.test(value)
		if (!isNumber) {
			return
		}

		const newItems = [...orderedItems]
		newItems[index].quantity = value

		// if (newItems[index].disableRemark) {
		// 	newItems[index].remark = ''
		// 	newItems[index].disableRemark = false
		// }

		setItems(newItems)

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onChangeQuotePrice = (value: string, index: number) => {
		const isNumber = /^[0-9]*\.?[0-9]*$/.test(value)
		if (!isNumber) {
			return
		}

		const newItems = [...orderedItems]
		newItems[index].quotePrice = value

		// if (newItems[index].disableRemark) {
		// 	newItems[index].remark = ''
		// 	newItems[index].disableRemark = false
		// }

		setItems(newItems)

		if (!hasChanged) {
			setHasChanged(true)
		}
	}

	const onEditItem = (index: number) => {
		const newItems = [...orderedItems]
		const item = newItems[index]
		if (item.isFixed) {
			setSelectingProductFormType('edit')
			setSelectedProduct(item)
			setSelectedProductIdx(index)
			setIsSelectingProduct(true)

			setHasChanged(true)
		}
	}

	const onDuplicateItem = (index: number) => {
		const newItems = [...orderedItems]
		const item = newItems[index]
		const newItem = { ...item, isCreate: true, disableRemark: true }

		newItems.splice(index + 1, 0, newItem)
		setItems(newItems)

		setHasChanged(true)
	}

	const onDeleteItem = (index: number) => {
		if (isCreate) {
			const newItems = [...orderedItems]
			newItems.splice(index, 1)
			setItems(newItems)
		} else {
			const newItems = [...orderedItems]
			newItems.splice(index, 1)
			setItems(newItems)
		}

		setHasChanged(true)
	}

	const handleGenerateQuotation = () => {
		const url = process.env.REACT_APP_BUYER_QUOTATION_URL + '/' + selected?.uuId
		setGeneratedUrl(url)
		setShowQuotationUrlModal(true)
	}

	const onGenerateQuotationForPublic = () => {
		if (selected?.isPublic === false) {
			Swal.fire({
				title: 'สร้างลิงก์ใบเสนอราคา',
				text: 'คุณจะไม่สามารถแก้ไขรายละเอียดใบเสนอราคาหลังจากสร้างลิงก์ใบเสนอราคา คุณต้องการสร้างลิงก์ใบเสนอราคาหรือไม่?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'ยืนยัน',
				cancelButtonText: 'ยกเลิก',
			}).then(result => {
				if (result.isConfirmed) {
					toast.info('กำลังสร้างลิงก์ใบเสนอราคา...')
					setIsSubmitting(true)
					publish(selected?.uuId).then(({ success, data }) => {
						if (!success) {
							toast.error(`ไม่สามารถสร้างลิงก์ใบเสนอราคาได้สำเร็จ : ${data}`)
							setIsSubmitting(false)
						} else {
							setIsSubmitting(false)
							fetchData()
							handleGenerateQuotation()
						}
					})
				}
			})
		} else {
			handleGenerateQuotation()
		}
	}

	const checkSameProductAndPropertiesInsideItemArrays = (): {
		isSame: boolean
		sameIdxs: number[]
	} => {
		const sameIdxs: number[] = []

		items.forEach((item, idx) => {
			const sameIdx = items.findIndex((i, iIdx) => {
				if (idx === iIdx) return false

				if (i.product?.id === item.product?.id) {
					const iOptions = (i.options ?? []).map((o: any) => o.taxonomyProductLineOption.id)
					const iAccessories = (i.accessories ?? []).map(
						(a: any) => a.taxonomyProductLineAccessory.id
					)
					const iThickness = i.thickness?.id
					const iOuterMaterial = i.outerMaterial?.id
					const iInnerMaterial = i.innerMaterial?.id
					const iPrintingOption = i.printingOption?.id
					const iForming = i.forming?.id

					const itemOptions = (item.options ?? []).map((o: any) => o.taxonomyProductLineOption.id)
					const itemAccessories = (item.accessories ?? []).map(
						(a: any) => a.taxonomyProductLineAccessory.id
					)
					const itemThickness = item.thickness?.id
					const itemOuterMaterial = item.outerMaterial?.id
					const itemInnerMaterial = item.innerMaterial?.id
					const itemPrintingOption = item.printingOption?.id
					const itemForming = item.forming?.id

					if (
						iOptions.length === itemOptions.length &&
						iAccessories.length === itemAccessories.length &&
						iThickness === itemThickness &&
						iOuterMaterial === itemOuterMaterial &&
						iInnerMaterial === itemInnerMaterial &&
						iPrintingOption === itemPrintingOption &&
						iForming === itemForming
					) {
						const isSameOptions = iOptions.every((o: any, oIdx: number) => o === itemOptions[oIdx])
						const isSameAccessories = iAccessories.every(
							(a: any, aIdx: number) => a === itemAccessories[aIdx]
						)

						if (isSameOptions && isSameAccessories) {
							return true
						}
					}
				}

				return false
			})

			if (sameIdx > -1) {
				sameIdxs.push(idx)
			}
		})

		return {
			isSame: sameIdxs.length > 0,
			sameIdxs,
		}
	}

	const onBack = () => {
		navigate('/documents/quotation')
	}

	const onSubmit = async () => {
		setIsSubmitting(true)

		if (hasChanged) {
			const { isSame, sameIdxs } = checkSameProductAndPropertiesInsideItemArrays()
			const formData = new FormData()

			// Insert financial
			formData.append('discount', financial.discount ?? 0)
			formData.append('isVat', financial.isVatCalculated.toString())

			let currentIdx = 0

			// Insert items
			items.forEach((item, idx) => {
				if (item.isFixed === true) {
					const findedIndex = sameIdxs.indexOf(idx)

					if (sameIdxs.includes(idx) && isSame && findedIndex !== 0) {
						const findFisrtItemIndexInItems = items.findIndex(
							i => i.product?.id === item.product?.id && i.isFixed
						)
						const nextIndex = findedIndex
						const lastItemIndex = sameIdxs[sameIdxs.length - 1]

						formData.append(
							`items[${findFisrtItemIndexInItems}][quantities][${nextIndex}][quantity]`,
							item?.quantity
						)
						formData.append(
							`items[${findFisrtItemIndexInItems}][quantities][${nextIndex}][quotePrice]`,
							item?.quotePrice
						)

						if (findedIndex === lastItemIndex) {
							currentIdx += 1
						}
					} else {
						formData.append(`items[${currentIdx}][isFixed]`, true.toString())
						formData.append(`items[${currentIdx}][remark]`, item?.remark)

						const options = item?.options ?? []
						const accessories = item?.accessories ?? []

						// Properties
						formData.append(`items[${currentIdx}][product]`, item?.product?.id)
						options.forEach((option: any, optionIdx: number) => {
							formData.append(
								`items[${currentIdx}][options][${optionIdx}]`,
								option.taxonomyProductLineOption.id
							)
						})
						accessories.forEach((accessory: any, accessoryIdx: number) => {
							formData.append(
								`items[${currentIdx}][accessories][${accessoryIdx}]`,
								accessory.taxonomyProductLineAccessory.id
							)
						})
						formData.append(`items[${currentIdx}][thickness]`, item?.thickness?.id ?? 0)
						formData.append(`items[${currentIdx}][outerMaterial]`, item?.outerMaterial?.id ?? 0)
						formData.append(`items[${currentIdx}][innerMaterial]`, item?.innerMaterial?.id ?? 0)
						formData.append(`items[${currentIdx}][printingOption]`, item?.printingOption?.id ?? 0)
						formData.append(`items[${currentIdx}][forming]`, item?.forming?.id ?? 0)

						// Dimension
						formData.append(`items[${currentIdx}][width]`, item?.dimension?.width)
						formData.append(`items[${currentIdx}][length]`, item?.dimension?.length)
						formData.append(`items[${currentIdx}][height]`, item?.dimension?.height)
						formData.append(`items[${currentIdx}][weightGram]`, item?.dimension?.weightGram)
						formData.append(`items[${currentIdx}][verticalStack]`, item?.dimension?.verticalStack)
						formData.append(`items[${currentIdx}][unit]`, item?.dimension?.unit)
						formData.append(
							`items[${currentIdx}][measurementApproach]`,
							item?.dimension?.measurementApproach
						)

						formData.append(`items[${currentIdx}][quantities][0][quantity]`, item?.quantity)
						formData.append(`items[${currentIdx}][quantities][0][quotePrice]`, item?.quotePrice)

						currentIdx += 1
					}
				} else {
					formData.append(`items[${currentIdx}][isFixed]`, false.toString())

					// Properties
					formData.append(`items[${currentIdx}][title]`, item?.title)
					formData.append(`items[${currentIdx}][quantity]`, item?.quantity)
					formData.append(`items[${currentIdx}][quotePrice]`, item?.quotePrice)
					formData.append(`items[${currentIdx}][remark]`, item?.remark)

					currentIdx += 1
				}
			})

			if (isCreate) {
				Swal.fire({
					title: 'สร้างใบเสนอราคา',
					text: 'คุณต้องการสร้างใบเสนอราคานี้หรือไม่?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'ยืนยัน',
					cancelButtonText: 'ยกเลิก',
				}).then(result => {
					if (result.isConfirmed) {
						toast.info('กำลังสร้างใบเสนอราคา...')
						create(formData).then(({ success, data }) => {
							if (!success) {
								toast.error(`สร้างใบเสนอราคาไม่สำเร็จ ${data}`)
								setIsSubmitting(false)
							} else {
								toast.success('สร้างใบเสนอราคาสำเร็จ')
								setIsSubmitting(false)
								navigate('/documents/quotation')
							}
						})
					} else {
						setIsSubmitting(false)
					}
				})
			} else if (id) {
				Swal.fire({
					title: 'แก้ไขใบเสนอราคา',
					text: 'คุณต้องการแก้ไขใบเสนอราคานี้หรือไม่?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'ยืนยัน',
					cancelButtonText: 'ยกเลิก',
				}).then(result => {
					if (result.isConfirmed) {
						toast.info('กำลังแก้ไขใบเสนอราคา...')
						update(id, formData).then(({ success, data }) => {
							if (!success) {
								toast.error(`แก้ไขใบเสนอราคาไม่สำเร็จ ${data}`)
								setIsSubmitting(false)
							} else {
								toast.success('แก้ไขใบเสนอราคาสำเร็จ')
								setIsSubmitting(false)
								setHasChanged(false)
								fetchData()
							}
						})
					} else {
						setIsSubmitting(false)
					}
				})
			}
		} else {
			setHasChanged(false)
			setIsSubmitting(false)
		}
	}

	const TABLE_COLUMNS: any[] = [
		{
			Header: 'ชื่อ',
			accessor: 'product',
			width: 600,
			minWidth: 600,
			Cell: TitleCell,
			canInput:
				isLoading ||
				isSubmitting ||
				(!isCreate && selected?.isPublic === true) ||
				(!isCreate && selected?.documentStatus !== '10'),
			onChangeItemTitle,
			onChangeItemRemark,
		},
		{
			Header: () => <div className="text-end">จำนวน</div>,
			headerRight: true,
			accessor: 'quantity',
			width: 200,
			minWidth: 200,
			Cell: QuantityInputCell,
			onChange: onChangeQuantity,
			canInput: isLoading || isSubmitting,
		},
		{
			Header: () => <div className="text-end">ราคาต่อหน่วย</div>,
			headerRight: true,
			accessor: 'quotePrice',
			width: 200,
			minWidth: 200,
			Cell: QuotePriceCell,
			onChange: onChangeQuotePrice,
			canInput: isLoading || isSubmitting,
		},
		{
			Header: () => <div className="text-end">ยอดรวม</div>,
			headerRight: true,
			accessor: 'totalPrice',
			width: 150,
			minWidth: 150,
			Cell: ({ row }: any) => {
				if ((selected?.documentStatus === '10' && selected?.isPublic === false) || isCreate) {
					let price = '0.00'

					const { quotePrice, quantity } = row.original
					const parsedQuotePrice = parseFloat(quotePrice)

					if (!isNaN(parsedQuotePrice)) {
						price = (parsedQuotePrice * parseFloat(quantity)).toFixed(2)
					}

					return <TotalPriceCell price={price as any} />
				} else {
					const prices = (row.original.quantities ?? []).map((item: any, index: number) => ({
						price: parseFloat(item.quotePrice ?? '0') * item.quantity,
					}))

					return <MultiplePriceCell prices={prices} />
				}
			},
		},
		{
			Header: '',
			accessor: 'action',
			width:
				isCreate || (selected?.documentStatus === '10' && selected?.isPublic === false) ? 60 : 0,
			minWidth:
				isCreate || (selected?.documentStatus === '10' && selected?.isPublic === false) ? 60 : 0,
			Cell: ({ row }: any) => {
				if (isCreate || (selected?.documentStatus === '10' && selected?.isPublic === false)) {
					return (
						<div className="d-flex flex-row align-items-center" style={{ gap: '12px' }}>
							{row.original?.isFixed && (
								<button
									className="btn btn-icon btn-custom btn-active-light rounded-circle"
									onClick={() => {
										onEditItem(row.index)
									}}>
									<img
										src="/media/icons/kumopack/edit-05-black.svg"
										alt="Dot Vertical Icon"
										width={20}
										height={20}
									/>
								</button>
							)}
							<button
								className="btn btn-icon btn-custom btn-active-light rounded-circle"
								onClick={() => {
									onDuplicateItem(row.index)
								}}>
								<img
									src="/media/icons/kumopack/copy-05.svg"
									alt="Dot Vertical Icon"
									width={20}
									height={20}
								/>
							</button>
							<button
								className="btn btn-icon btn-custom btn-active-light rounded-circle"
								onClick={() => {
									onDeleteItem(row.index)
								}}>
								<img
									src="/media/icons/kumopack/delete.svg"
									alt="Dot Vertical Icon"
									width={20}
									height={20}
								/>
							</button>
						</div>
					)
				} else {
					return null
				}
			},
			disableSortBy: true,
		},
	]

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	useEffect(() => {
		setupTable()
		setShowContentToolbar(false)
		setCustomContentToolbar(null)
		updateSorting('product', false)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			setGeneratedUrl('')
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		currentLanguage,
		canSelectProductLine,
		canGenerateQuotation,
		canEditQuotation,
		isAccepted,
		isSubmitting,
		isLoading,
		isCreate,
		isSelectingProduct,
		setIsSelectingProduct,
		documentStatusStyles,
		pageTitle,
		pageBreachCrumbs,
		acceptedBuyer,
		items: orderedItems,
		selectingProductFormType,
		selectedProduct,
		financial,
		netBeforeVat,
		net,
		vatAmount,
		generatedUrl,
		onChatBuyer,
		onClickAddNewItem,
		onAddNewItem,
		onBackFromSelectNewItem,
		onChangeDiscount,
		onChangeVatCalculated,
		onConfirmAddProduct,
		onGenerateQuotationForPublic,
		onBack,
		onSubmit,
		TABLE_COLUMNS,
	}
}

export default ViewModel
